'use client';

import { ApolloClient, InMemoryCache, createHttpLink, from } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';

const isBrowser = typeof window !== 'undefined';

// Server-side uses WORDPRESS_URL (can be HTTP to local nginx), client uses NEXT_PUBLIC
const WORDPRESS_URL = isBrowser
  ? (process.env.NEXT_PUBLIC_WORDPRESS_URL || 'http://localhost')
  : (process.env.WORDPRESS_URL || process.env.NEXT_PUBLIC_WORDPRESS_URL || 'http://localhost');
const GRAPHQL_URI = `${WORDPRESS_URL}/graphql`;

const httpLink = createHttpLink({
  uri: GRAPHQL_URI,
  credentials: 'same-origin',
});

const authLink = setContext((_, { headers }) => {
  const token = isBrowser ? localStorage.getItem('auth-token') : null;

  // Only attach custom headers when we actually have values. Empty
  // X-WC-Key/Secret headers force a CORS preflight that fails because
  // WP-GraphQL doesn't advertise those in Access-Control-Allow-Headers —
  // and GraphQL doesn't need them anyway (WC REST is the consumer-key
  // endpoint; GraphQL uses Bearer auth).
  const h: Record<string, string> = { ...headers };
  if (token) {
    h.authorization = `Bearer ${token}`;
  }
  const wcKey = process.env.NEXT_PUBLIC_WOOCOMMERCE_CONSUMER_KEY || process.env.WOOCOMMERCE_CONSUMER_KEY || '';
  const wcSecret = process.env.NEXT_PUBLIC_WOOCOMMERCE_CONSUMER_SECRET || process.env.WOOCOMMERCE_CONSUMER_SECRET || '';
  if (wcKey)    h['X-WC-Key'] = wcKey;
  if (wcSecret) h['X-WC-Secret'] = wcSecret;

  return { headers: h };
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message }) => {
      // Suppress in production — SSR data is authoritative
      if (process.env.NODE_ENV !== 'production') {
        console.error(`GraphQL error: ${message}`);
      }
    });
  }

  if (networkError) {
    // Suppress 403 errors (CF WAF blocks client-side GraphQL POST) — SSR handles data
    if ('statusCode' in networkError && networkError.statusCode === 403) return;
    if ('statusCode' in networkError && networkError.statusCode === 401) {
      if (isBrowser) {
        localStorage.removeItem('auth-token');
      }
    }
  }
});

let clientInstance: ApolloClient<any> | null = null;

function createClient() {
  if (clientInstance) {
    return clientInstance;
  }

  const cache = new InMemoryCache({
    typePolicies: {
      Product: {
        keyFields: ['id'],
      },
      ProductCategory: {
        keyFields: ['id'],
      },
      Query: {
        fields: {
          products: {
            // Include `first`/`after` in the key so GET_PRODUCTS(first:50) and
            // GET_FEATURED_PRODUCTS's fallback GET_PRODUCTS(first:4) cache
            // separately. Without this, the last-completing query overwrites
            // the other and one of the pages renders a truncated list.
            keyArgs: ['where', 'orderBy', 'first', 'after'],
            merge(existing: any = { nodes: [] }, incoming: any) {
              if (!incoming || !incoming.nodes) {
                return existing;
              }
              return { ...incoming };
            },
          },
          productCategories: {
            keyArgs: ['where'],
            merge(existing: any = { nodes: [] }, incoming: any) {
              if (!incoming || !incoming.nodes) {
                return existing;
              }
              return {
                ...incoming,
                nodes: [...(existing.nodes || []), ...incoming.nodes],
              };
            },
          },
        },
      },
    },
  });

  if (!isBrowser) {
    // Use WORDPRESS_URL for server-side (HTTP to local nginx, bypasses CF/proxy)
    const ssrUrl = (process.env.WORDPRESS_URL || process.env.NEXT_PUBLIC_WORDPRESS_URL || 'http://localhost') + '/graphql';
    clientInstance = new ApolloClient({
      ssrMode: true,
      link: createHttpLink({
        uri: ssrUrl,
      }),
      cache,
    });
    return clientInstance;
  }

  clientInstance = new ApolloClient({
    link: from([errorLink, authLink, httpLink]),
    cache,
    defaultOptions: {
      watchQuery: {
        errorPolicy: 'all',
      },
      query: {
        errorPolicy: 'all',
      },
    },
    devtools: {
      enabled: process.env.NODE_ENV !== 'production',
    },
  });

  return clientInstance;
}

const client = createClient();

export default client;

export function setAuthToken(token: string) {
  if (isBrowser) {
    localStorage.setItem('auth-token', token);
  }
}

export function clearAuthToken() {
  if (isBrowser) {
    localStorage.removeItem('auth-token');
  }
  if (clientInstance) {
    clientInstance.resetStore();
  }
}

export function isAuthenticated() {
  if (!isBrowser) return false;
  return !!localStorage.getItem('auth-token');
}

export interface AuthUser {
  id: number;
  email: string;
  username: string;
  displayName: string;
  firstName?: string;
  lastName?: string;
  avatar?: string;
}

export function setAuthUser(user: AuthUser) {
  if (isBrowser) {
    localStorage.setItem('auth-user', JSON.stringify(user));
  }
}

export function getAuthUser(): AuthUser | null {
  if (!isBrowser) return null;
  const raw = localStorage.getItem('auth-user');
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

export function clearAuthUser() {
  if (isBrowser) {
    localStorage.removeItem('auth-user');
  }
}
